/* Вид для чтения итога «Текста» разметкой шаблона (resHTML движка, engine.ts раздел 7) из документа сервера (TextDocument, text-result-model):
   расшифровка — реплики со спикером и моно-временем (длинная свёрнута до «Показать всё»), текст ролика — абзацы с началом, пересказ или тезисы,
   описание товара — четыре блока со счётчиками и «Скопировать» у каждого, промпт — блок (по пунктам — «ключ: значение»). Классы и порядок узлов —
   шаблона: стили экрана (text.css) и сверка DOM те же. Подписи — словарём профиля; демо-итогов шаблона здесь нет. */
import { uiFormat, uiText } from '../../i18n';
import { choiceLabels } from './native/text-model';
import { speakerName, type TextDocument } from './native/text-result-model';

/** Разметочные помощники шаблона (крючки движка) */
export interface ReadingKit {
  esc(value: string): string;
  rhHTML(b: string, sm: string, i?: number): string;
  parasHTML(L: Array<[number, string]>, tc: boolean): string;
  ulHTML(L: string[]): string;
  cp(text: string): number;
  /** Подпись значения выбора по списку шаблона (optName) */
  option(list: string, value: string): string;
  /** Название стиля текста описания (TONES) */
  tone(id: string): string;
}
/** Что известно о заказе помимо документа: вид, выборы снимка, файл источника */
export interface ReadingContext {
  kind: string;
  spk?: boolean;
  tc?: boolean;
  lang?: string;
  ytKind?: string;
  market?: string;
  tone?: string;
  style?: string;
  plang?: string;
  file?: { name?: string; dur?: number } | null;
  /** «Показать всё» уже нажато */
  open?: boolean;
}

const mmss = (sec: number) => {
  const s = Math.max(0, Math.round(sec || 0));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};
function plural(n: number, one: string, few: string, many: string) {
  const m = Math.abs(n) % 100,
    d = m % 10;
  return m > 10 && m < 20 ? many : d === 1 ? one : d >= 2 && d <= 4 ? few : many;
}
const count = (n: number, one: string, few: string, many: string) => `${n} ${uiText(plural(n, one, few, many))}`;
const paragraphs = (text: string) =>
  text
    .replace(/\r/g, '')
    .split(/\n{2,}|\n/)
    .map(line => line.trim())
    .filter(Boolean);

/** Текст блока «Скопировать» (весь итог для «Скопировать», «Скачать» и «Что дальше» — textDocumentPlain) */
export function readingHTML(doc: TextDocument, ctx: ReadingContext, k: ReadingKit): string {
  const { esc } = k;
  if (ctx.kind === 'stt') {
    /* реплики: у сервера — сегменты (спикер, начало); без сегментов — абзацы расшифровки */
    const lines = doc.segments.length
      ? doc.segments.map(s => ({ who: s.speaker ?? '', at: s.start_ms !== undefined ? s.start_ms / 1000 : null, text: s.text }))
      : paragraphs(doc.transcript).map(text => ({ who: '', at: null as number | null, text }));
    const speakers = [...new Set(lines.map(l => l.who).filter(Boolean))];
    const spk = ctx.spk !== false && speakers.length > 0;
    const tc = ctx.tc !== false && lines.some(l => l.at !== null);
    const n = lines.length,
      cut = n > 12 && !ctx.open;
    const lastEnd = doc.segments.reduce((max, s) => Math.max(max, (s.end_ms ?? s.start_ms ?? 0) / 1000), 0);
    const dur = ctx.file?.dur || lastEnd;
    const language = doc.language ? (choiceLabels[doc.language] ?? doc.language) : ctx.lang && ctx.lang !== 'auto' ? k.option('lang', ctx.lang) : '';
    const meta = [ctx.file?.name ?? '', dur ? mmss(dur) : '', spk ? count(speakers.length, 'собеседник', 'собеседника', 'собеседников') : '', language ? uiText(language).toLowerCase() : '']
      .filter(Boolean)
      .join(' · ');
    const row = (l: (typeof lines)[number]) => {
      const s = speakers.indexOf(l.who);
      const who = spk && l.who ? `<span class="av s${Math.max(0, s)}">${Math.max(0, s) + 1}</span><b>${esc(speakerName(l.who))}</b>` : '';
      const at = tc && l.at !== null ? `<span class="tc mono">${mmss(l.at)}</span>` : '';
      return `<div class="tt-ln">${spk || tc ? `<div class="w">${who}${at}</div>` : ''}<p>${esc(l.text)}</p></div>`;
    };
    return (
      `<div class="tt-rd${cut ? ' cut' : ''}" id="ttRd">${k.rhHTML(uiText('Расшифровка'), meta)}<div class="tt-tr">${lines.map(row).join('')}</div>` +
      `${cut ? `<button class="tt-more" type="button" data-tt-more>${esc(uiFormat('Показать всё · {0}', count(n, 'реплика', 'реплики', 'реплик')))}</button>` : ''}</div>`
    );
  }
  if (ctx.kind === 'yt') {
    const kind = ctx.ytKind ?? 'full';
    let body: string;
    if (kind === 'th') {
      const items = (doc.segments.length ? doc.segments.map(s => s.text) : paragraphs(doc.transcript)).map(line => line.replace(/^\s*[•\-–*]\s*/, ''));
      body = k.ulHTML(items);
    } else if (kind === 'full' && doc.segments.some(s => s.start_ms !== undefined))
      body = k.parasHTML(doc.segments.map(s => [(s.start_ms ?? 0) / 1000, s.text] as [number, string]), true);
    else {
      const items = doc.segments.length ? doc.segments.map(s => s.text) : paragraphs(doc.transcript);
      body = `<div class="tt-tr">${items.map(x => `<p class="tt-p">${esc(x)}</p>`).join('')}</div>`;
    }
    /* название и длительность ролика — не от сервера (Q-T1): подпись блока без них */
    return `<div class="tt-rd">${k.rhHTML(k.option('ytkind', kind), '')}${body}</div>`;
  }
  if (ctx.kind === 'seo') {
    const block = (label: string) => doc.blocks.find(b => b.label === label);
    const title = block('Заголовок'),
      desc = block('Описание'),
      bullets = block('Преимущества'),
      keys = block('Ключевые слова');
    const market = ctx.market ? k.option('market', ctx.market) : '';
    const parts: string[] = [];
    if (title)
      parts.push(
        `<div class="tt-rd">${k.rhHTML(uiText('Заголовок'), [market, count(Array.from(title.text).length, 'знак', 'знака', 'знаков')].filter(Boolean).join(' · '), k.cp(title.text))}<p class="tt-h">${esc(title.text)}</p></div>`,
      );
    const descText = desc?.text ?? (!doc.blocks.length ? doc.transcript : '');
    if (descText) {
      const tone = ctx.tone ? uiFormat('Стиль «{0}»', k.tone(ctx.tone)) : '';
      parts.push(
        `<div class="tt-rd">${k.rhHTML(uiText('Описание'), [tone, count(Array.from(descText).length, 'знак', 'знака', 'знаков')].filter(Boolean).join(' · '), k.cp(descText))}<p class="tt-p">${esc(descText)}</p></div>`,
      );
    }
    if (bullets?.items?.length)
      parts.push(
        `<div class="tt-rd">${k.rhHTML(uiText('Преимущества'), count(bullets.items.length, 'пункт', 'пункта', 'пунктов'), k.cp(bullets.text))}${k.ulHTML(bullets.items)}</div>`,
      );
    if (keys?.items?.length)
      parts.push(
        `<div class="tt-rd">${k.rhHTML(uiText('Ключевые слова'), count(keys.items.length, 'фраза', 'фразы', 'фраз'), k.cp(keys.items.join(', ')))}<div class="tt-kw">${keys.items.map(x => `<span>${esc(x)}</span>`).join('')}</div></div>`,
      );
    return parts.join('\n      ');
  }
  /* промпт из фото и видео: блок промпта; «По пунктам» — строки «ключ: значение» */
  const p = doc.variants[0] ?? doc.transcript;
  const kv = p.includes('\n') && p.split('\n').every(line => /^[^:]{2,16}:\s/.test(line));
  const body = kv
    ? `<dl class="tt-kv">${p
        .split('\n')
        .map(line => {
          const j = line.indexOf(':');
          return `<div><dt>${esc(line.slice(0, j))}</dt><dd>${esc(line.slice(j + 1).trim())}</dd></div>`;
        })
        .join('')}</dl>`
    : `<p class="tt-p">${esc(p)}</p>`;
  const meta = [ctx.style ? k.option('pstyle', ctx.style) : '', ctx.plang ? k.option('plang', ctx.plang) : ''].filter(Boolean).join(' · ');
  return `<div class="tt-rd">${k.rhHTML(uiText('Промпт'), meta, k.cp(p))}${body}</div>`;
}

/** Варианты на месте из документа: подпись сервера или «Вариант N», текст; без вариантов — текст итога одной карточкой */
export function variantsOf(doc: TextDocument): Array<{ h: string; t: string }> {
  const texts = doc.variants.length ? doc.variants : [doc.transcript].filter(Boolean);
  return texts.map((t, i) => ({ h: doc.titles[i] || uiFormat('Вариант {0}', i + 1), t }));
}
